import {
  useGetPlatformDiagnostics,
  useListPlatformJobRuns,
  useListPlatformJobs,
  useSendTestEmail,
  type JobRunOut,
  type RecentProblemOut,
} from '@myrentalmate/api-client';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { TabbedLayout } from '../../components/TabbedLayout';
import { okData, okItems } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/format';
import { RunResult, seconds } from './jobLabels';
import { TablePager, useTablePage } from '../../components/TablePager';

const TABS = [
  { to: '/platform/diagnostics', label: 'Recent Problems' },
  { to: '/platform/diagnostics/runs', label: 'Job Run History' },
  { to: '/platform/diagnostics/email', label: 'Email Test' },
];

export function DiagnosticsLayout() {
  return <TabbedLayout title="Diagnostics" label="Diagnostics sections" tabs={TABS} />;
}

const PROBLEM_COLUMNS: DataColumn<RecentProblemOut>[] = [
  {
    label: 'Time',
    render: (r) => <Box sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.ts)}</Box>,
  },
  {
    label: 'Level',
    render: (r) => (
      <Chip
        size="small"
        color={r.level === 'ERROR' || r.level === 'CRITICAL' ? 'error' : 'warning'}
        label={r.level}
      />
    ),
  },
  { label: 'Message', render: (r) => <Box sx={{ overflowWrap: 'anywhere' }}>{r.message}</Box> },
];

export function RecentProblemsPage() {
  const diagnostics = okData(useGetPlatformDiagnostics().data);
  return (
    <Stack spacing={1}>
      <Typography variant="h6">Recent Warnings and Errors</Typography>
      <Typography variant="body2" color="text.secondary">
        From the running copy of the app since it last started. The full history is in Log
        Analytics.
      </Typography>
      {diagnostics?.recent_problems.length === 0 ? (
        <Typography color="text.secondary">None since it started.</Typography>
      ) : (
        <DataTable
          columns={PROBLEM_COLUMNS}
          rows={diagnostics?.recent_problems ?? []}
          rowKey={(r) => `${r.ts}-${r.message}`}
        />
      )}
    </Stack>
  );
}

export function JobRunHistoryPage() {
  const [name, setName] = useState('');
  const jobs = okData(useListPlatformJobs().data)?.items ?? [];
  const paging = useTablePage();
  const query = useListPlatformJobRuns({ ...paging.params, ...(name ? { name } : {}) });
  const runs = okItems(query.data);
  const labelOf = (job: string) => jobs.find((j) => j.name === job)?.label ?? job;
  const columns: DataColumn<JobRunOut>[] = [
    { label: 'Job', render: (r) => labelOf(r.name) },
    { label: 'Started', render: (r) => formatDateTime(r.started_at) },
    {
      label: 'Duration',
      render: (r) =>
        r.finished_at
          ? seconds(
              Math.round(
                (new Date(`${r.finished_at}Z`).getTime() - new Date(`${r.started_at}Z`).getTime()) /
                  1000,
              ),
            )
          : '–',
    },
    { label: 'Started By', render: (r) => (r.trigger === 'manual' ? 'Run Now' : 'Schedule') },
    { label: 'Result', render: (r) => <RunResult run={r} /> },
    { label: 'Error', render: (r) => r.error ?? '–' },
  ];
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Scheduled Job Runs
        </Typography>
        <TextField
          select
          size="small"
          label="Job"
          value={name}
          sx={{ minWidth: 220 }}
          onChange={(e) => {
            setName(e.target.value);
            paging.setPage(0);
          }}
        >
          <MenuItem value="">All Jobs</MenuItem>
          {jobs.map((job) => (
            <MenuItem key={job.name} value={job.name}>
              {job.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      {runs.length === 0 ? (
        <Typography color="text.secondary">No job has run yet.</Typography>
      ) : (
        <>
          <DataTable columns={columns} rows={runs} rowKey={(r) => r.id} />
          <TablePager paging={paging} total={okData(query.data)?.total ?? 0} />
        </>
      )}
    </Stack>
  );
}

export function EmailTestPage() {
  const testEmail = useSendTestEmail();
  const sent = okData(testEmail.data)?.message;
  return (
    <Paper variant="outlined" sx={{ p: 3, maxWidth: 640 }}>
      <Typography variant="h6">Email Test</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Sends a test email to your own address through SendGrid.
      </Typography>
      <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Button
          variant="outlined"
          disabled={testEmail.isPending}
          onClick={() => testEmail.mutate()}
        >
          {testEmail.isPending ? 'Sending…' : 'Send Test Email'}
        </Button>
        {sent && <Alert severity="info">{sent}</Alert>}
        {testEmail.isError && <Alert severity="error">{apiErrorMessage(testEmail.error)}</Alert>}
      </Stack>
    </Paper>
  );
}
